import useTodaySnapshot from '@/components/toto/useTodaySnapshot';
import TodayNavigation from '@/components/toto/TodayNavigation';
import EODReportBento, {EODReportShell} from './EODReportBento';
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TotoAdaptiveDock } from "@/components/toto/TotoAdaptiveSurface";
import { Skeleton } from "@/components/Skeleton";
import { toast } from "sonner";
import { useSearchParams, useNavigate } from "react-router-dom";
import "./eod-space.css";
import {
  Check, ChevronDown, Sparkles, Send, Plus, Trash2, RefreshCw,
  TrendingUp, AlertCircle, HeartHandshake, CreditCard, Package, HeartPulse,
  Construction, Search as SearchIcon, ClipboardCheck,
} from "lucide-react";

const STEPS = [
  { key: "business",    label: "Business Performance", shortLabel: "Business",    icon: TrendingUp },
  { key: "attention",   label: "Attention Items",       shortLabel: "Attention",   icon: AlertCircle },
  { key: "recovery",    label: "Guest Recovery",        shortLabel: "Recovery",    icon: HeartHandshake },
  { key: "membership",  label: "Membership Activity",   shortLabel: "Memberships", icon: CreditCard },
  { key: "inventory",   label: "Inventory",             shortLabel: "Inventory",   icon: Package },
  { key: "recognition", label: "Team Recognition",      shortLabel: "Recognition", icon: HeartPulse },
  { key: "roadblocks",  label: "Roadblocks",            shortLabel: "Roadblocks",  icon: Construction },
  { key: "lostfound",   label: "Lost & Found",          shortLabel: "Lost & Found",icon: SearchIcon },
  { key: "recap",       label: "Recap & Submit",        shortLabel: "Handoff",     icon: ClipboardCheck },
];

const reportStatus = (status) => typeof status === "string" && status ? status.replace(/_/g, " ") : "Status unavailable";

const money = (n) => n === null || n === undefined || n === "" ? "—" : `$${Number(n).toFixed(2)}`;

export default function EODWorkflow() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loadedReport, setReport] = useTodaySnapshot('eod:report',null);
  const [selectedReport, setSelectedReport] = useTodaySnapshot('eod:selection',null);
  const [loadError, setLoadError] = useState("");
  const leadershipView = loadedReport?.leadership_view === true;
  const report = selectedReport || (leadershipView?loadedReport?.reports?.[0]:loadedReport);
  const [reference, setReference] = useTodaySnapshot('eod:reference',null);
  const [stepIdx, setStepIdx] = useState(() => {
    const requestedStep = searchParams.get("step");
    const index = STEPS.findIndex((step) => step.key === requestedStep);
    return index >= 0 ? index : 0;
  });
  const [busy, setBusy] = useState(false);
  const [verifying,setVerifying]=useState(true);
  const mutationLock = useRef(false);

  const load = async () => {
    setLoadError("");
    setVerifying(true);
    try {
      const [r, ref] = await Promise.all([
        api.get("/eod/today"),
        api.get("/eod/reference"),
      ]);
      setReport(r.data);
      setSelectedReport(previous => r.data.leadership_view === true
        ? (r.data.reports || []).find(item => item.id === previous?.id && item.boutique_code === previous?.boutique_code && item.date === previous?.date) || r.data.reports?.[0] || null
        : null);
      setReference(ref.data);
    } catch (e) {
      setReport(null);setReference(null);setSelectedReport(null);
      setLoadError(e.response?.data?.detail || "Could not load today's report.");
    } finally {setVerifying(false);}
  };
  useEffect(() => { load(); }, []);

  const call = async (fn) => {
    if(mutationLock.current)return;
    mutationLock.current=true;setBusy(true);
    try { const r = await fn(); if (r?.data) setReport(r.data); return r?.data; }
    catch (e) { toast.error(e.response?.data?.detail || "Something didn't save."); }
    finally { mutationLock.current=false;setBusy(false); }
  };

  const todayNavigation = <TodayNavigation user={user} current="eod" navigate={navigate}/>;

  const shell = content => <EODReportShell report={report} todayNavigation={todayNavigation} dock={<TotoAdaptiveDock active="today"/>} loading={!loadError && (!report || !reference)}>{content}</EODReportShell>;

  // Reserve the report's card layout, rather than removing its work area while
  // data is unavailable. This also keeps WebKit's phone scrollbar consistent.
  const placeholder = <div className={`eod-report__placeholder${loadError || (leadershipView && !selectedReport)?' is-unavailable':''}`} aria-hidden="true">
    <Skeleton className="eod-report__placeholder-toolbar"/>
    <div className="eod-report__placeholder-cards"><Skeleton className="eod-report__placeholder-card"/><Skeleton className="eod-report__placeholder-card"/></div>
    <Skeleton className="eod-report__placeholder-goals"/>
  </div>;
  if (loadError) return shell(<><div role="alert"><p>{loadError}</p><button type="button" onClick={load}>Try again</button></div>{placeholder}</>);
  if ((!loadedReport || !reference) || (!leadershipView && !report)) return shell(<div role="status" aria-label="Loading End of Day report">{placeholder}</div>);
  if (leadershipView && !report) return shell(<><div><p>No boutique reports are available yet.</p><button type="button" className="eod-report__refresh" onClick={load}>Refresh reports</button></div>{placeholder}</>);

  const step = STEPS[stepIdx];
  const StepIcon = step.icon;
  const editable = !verifying && !leadershipView && (report.status === "in_progress" || report.status === "reopened");

  const reportSelection = leadershipView && loadedReport.reports.length > 1 ? <select className="eod-report__selection" aria-label="Boutique report" disabled={busy} value={Math.max(0,loadedReport.reports.findIndex(item=>item.id===report.id))} onChange={event=>setSelectedReport(loadedReport.reports[Number(event.target.value)])}>
    {loadedReport.reports.map((item,index)=><option key={item.id || `${item.boutique_code}-${item.date}`} value={index}>{item.boutique_code} · {item.date} · {reportStatus(item.status)}</option>)}
  </select> : null;
  return shell(<EODReportBento embedded key={report.id || `${report.boutique_code}-${report.date}`} report={report} todayNavigation={todayNavigation} onDone={()=>navigate('/home')} dock={<TotoAdaptiveDock active="today"/>} reference={reference} editable={editable} busy={busy||verifying} call={call} onRefresh={load} reportSelection={reportSelection} renderExtra={field=><ListStep report={report} editable={editable} call={call} field={field} form={field==='attention_items'?AttentionForm:field==='recognitions'?RecognitionForm:RoadblockForm} Item={field==='attention_items'?AttentionItem:field==='recognitions'?RecognitionItem:RoadblockItem} label={field==='attention_items'?'attention items':field==='recognitions'?'recognitions':'roadblocks'} reference={reference}/>}/>);

}


/* ─── Step 1 · Business Performance ────────────────── */
const METRICS = [
  { key: "guests_served",     label: "Guests served" },
  { key: "memberships_sold",  label: "Memberships sold" },
  { key: "retail_sales",      label: "Retail sales ($)" },
  { key: "enhancements_pct",  label: "Enhancements %" },
  { key: "rebooking_pct",     label: "Rebooking %" },
  { key: "recurring_members", label: "Recurring members" },
];

function BusinessStep({ report, editable, call }) {
  const metrics = report.business_performance?.metrics || {};
  const source = report.business_performance?.source || "manual";
  const [drafts, setDrafts] = useState({});
  const [reasons, setReasons] = useState({});

  const setDraft = (k, v) => setDrafts((p) => ({ ...p, [k]: v }));
  const setReason = (k, v) => setReasons((p) => ({ ...p, [k]: v }));

  const saveAll = async () => {
    const patch = {};
    for (const k of Object.keys(drafts)) {
      patch[k] = { reported_value: Number(drafts[k]), override_reason: reasons[k] || null };
    }
    if (Object.keys(patch).length === 0) { toast.info("Nothing to save."); return; }
    await call(() => api.patch("/eod/today/business", { metrics: patch }));
    setDrafts({}); setReasons({});
  };

  return (
    <div className="space-y-4" data-testid="business-step">
      <p className="text-[13px] text-stone-600 leading-relaxed">
        The dashboard already knows most of this. Only correct a number if something looks wrong.
      </p>
      <div className="eod-business__source rounded-xl bg-stone-50 border border-stone-100 px-3 py-2 text-[11px] text-stone-600">
        Source: {source === "zenoti" ? "Zenoti daily report" : "No Zenoti report yet — values are blank."}
      </div>
      <div className="eod-business__grid">
        {METRICS.map((m) => {
          const cell = metrics[m.key] || {};
          const overridden = !!cell.override_reason;
          const val = drafts[m.key] ?? cell.reported_value ?? "";
          return (
            <div key={m.key} className="eod-business__metric rounded-xl border border-stone-100 p-3">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[12px] text-stone-700">{m.label}</span>
                {cell.source_value !== null && cell.source_value !== undefined && (
                  <span className="text-[10px] text-stone-400">source: {cell.source_value}</span>
                )}
              </div>
              <input
                type="number" step="0.01" disabled={!editable}
                value={val}
                onChange={(e) => setDraft(m.key, e.target.value)}
                data-testid={`bp-${m.key}`}
                className="eod-business__value w-full px-3 py-1.5 rounded-lg subtle-border text-sm bg-white disabled:bg-stone-50"
              />
              {drafts[m.key] !== undefined && cell.source_value !== null && Number(drafts[m.key]) !== cell.source_value && (
                <input
                  placeholder="Why the correction?"
                  value={reasons[m.key] || ""}
                  onChange={(e) => setReason(m.key, e.target.value)}
                  data-testid={`bp-reason-${m.key}`}
                  className="mt-2 w-full px-3 py-1.5 rounded-lg subtle-border text-[12px] italic"
                />
              )}
              {overridden && drafts[m.key] === undefined && (
                <div className="text-[11px] text-amber-800 mt-1">
                  Overridden by {cell.overridden_by} · <em>{cell.override_reason}</em>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {editable && Object.keys(drafts).length > 0 && (
        <button onClick={saveAll} data-testid="bp-save" className="px-4 py-2 rounded-full bg-stone-900 text-white text-xs hover:bg-stone-800">
          Save corrections
        </button>
      )}
    </div>
  );
}


/* ─── Generic list step ────────────────── */
function ListStep({ report, editable, call, field, form: FormComp, Item: ItemComp, label, reference }) {
  const items = report[field] || [];
  const noneKey = `${field}_none_reported`;
  const noneReported = !!report[noneKey];
  const endpointMap = {
    attention_items: "attention-items",
    guest_recovery: "guest-recovery",
    membership_activity: "membership-activity",
    inventory: "inventory",
    recognitions: "recognitions",
    roadblocks: "roadblocks",
    lost_and_found: "lost-found",
  };
  const endpoint = endpointMap[field];

  const addItem = async (payload) => {
    return await call(() => api.post(`/eod/today/${endpoint}`, payload));
  };
  const removeItem = async (id) => {
    await call(() => api.delete(`/eod/today/${endpoint}/${id}`));
  };
  const toggleNone = async () => {
    await call(() => api.patch("/eod/today/none-reported", { section: field, none_reported: !noneReported }));
  };

  return (
    <div className="space-y-4" data-testid={`list-step-${field}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-[13px] text-stone-600 leading-relaxed">
          {items.length === 0 && !noneReported
            ? `Add anything that happened, or confirm there's nothing to report.`
            : `${items.length} ${label} logged.`}
        </p>
        {items.length === 0 && editable && (
          <label className="inline-flex items-center gap-1.5 text-[12px] text-stone-700 cursor-pointer">
            <input type="checkbox" checked={noneReported} onChange={toggleNone} data-testid={`none-${field}`} />
            <span>Nothing to report</span>
          </label>
        )}
      </div>

      {items.length > 0 && (
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.id} className="rounded-xl bg-white subtle-border p-3">
              <div className="flex items-start justify-between gap-3">
                <ItemComp item={item} />
                {editable && (
                  <button onClick={() => removeItem(item.id)} data-testid={`remove-${field}-${item.id}`} className="text-stone-400 hover:text-rose-600">
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {editable && !noneReported && <FormComp onSubmit={addItem} reference={reference} />}
    </div>
  );
}


/* ─── Sub-forms & item renderers ────────────────── */
function useForm(initial) {
  const [f, setF] = useState(initial);
  return [f, (k, v) => setF((p) => ({ ...p, [k]: v })), () => setF(initial)];
}

function VesselMenu({ value, onChange, options, label, testId, disabled = false, className = "" }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const selected = options.find((option) => String(option.value) === String(value));

  useEffect(() => {
    const closeOnOutsidePress = (event) => {
      if (!menuRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsidePress);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsidePress);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  return (
    <div className={`eod-menu ${className}`} ref={menuRef} data-testid={testId}>
      <button type="button" className="eod-menu__trigger" aria-label={label} aria-haspopup="listbox" aria-expanded={open} disabled={disabled} onClick={() => setOpen((isOpen) => !isOpen)}>
        <span>{selected?.label || label}</span><ChevronDown size={14} aria-hidden="true" />
      </button>
      {open && !disabled && (
        <div className="eod-menu__options" role="listbox" aria-label={label}>
          {options.map((option) => {
            const isSelected = String(option.value) === String(value);
            return <button type="button" role="option" aria-selected={isSelected} key={option.value} className="eod-menu__option" onClick={() => { onChange(option.value); setOpen(false); }}><span>{option.label}</span>{isSelected && <Check size={13} aria-hidden="true" />}</button>;
          })}
        </div>
      )}
    </div>
  );
}

function AttentionForm({ onSubmit, reference }) {
  const owners = reference?.attention_owners || [];
  const [f, set, reset] = useForm({ category: "guest", description: "", priority: "medium", owner_id: "", owner_name: "", requires_follow_up: true });
  const chooseOwner = (ownerId) => {
    const owner = owners.find((candidate) => candidate.user_id === ownerId);
    set("owner_id", owner?.user_id || "");
    set("owner_name", owner?.name || "");
  };
  return (
    <div className="rounded-xl border border-dashed border-stone-200 p-3 space-y-2" data-testid="form-attention">
      <div className="grid sm:grid-cols-2 gap-2">
        <VesselMenu value={f.category} onChange={(value) => set("category", value)} testId="ai-category" label="Attention category" options={(reference?.item_categories || []).map((value) => ({ value, label: value }))} />
        <VesselMenu value={f.priority} onChange={(value) => set("priority", value)} testId="ai-priority" label="Attention priority" options={(reference?.priorities || []).map((value) => ({ value, label: value }))} />
      </div>
      <input value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="ai-desc" placeholder="What tomorrow's team should know" className="w-full px-3 py-1.5 rounded-lg subtle-border text-sm" />
      <VesselMenu value={f.owner_id} onChange={chooseOwner} testId="ai-owner" label={owners.length ? "Choose the teammate responsible for follow-up" : "No active teammates are available"} options={owners.map((owner) => ({ value: owner.user_id, label: `${owner.name}${owner.title ? ` · ${owner.title}` : owner.role ? ` · ${owner.role.replace(/_/g, " ")}` : ""}` }))} disabled={!owners.length} />
      <button onClick={async () => { if (!f.description.trim() || !f.owner_id) return; if(await onSubmit(f))reset(); }} data-testid="ai-add" disabled={!owners.length} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-stone-900 text-white text-xs disabled:opacity-40">
        <Plus size={11} /> Add item
      </button>
    </div>
  );
}
function AttentionItem({ item }) {
  const p = item.priority || "low";
  const tint = p === "critical" ? "bg-rose-100 text-rose-900" : p === "high" ? "bg-amber-100 text-amber-900" : p === "medium" ? "bg-stone-100 text-stone-700" : "bg-stone-50 text-stone-500";
  return (
    <div className="flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${tint}`}>{p}</span>
        <span className="text-[11px] text-stone-500">{item.category}</span>
      </div>
      <div className="text-sm text-stone-900 mt-1">{item.description}</div>
      {item.owner_name && <div className="text-[11px] text-stone-500 mt-0.5">Owner · {item.owner_name}</div>}
    </div>
  );
}

function GRForm({ onSubmit }) {
  const [f, set, reset] = useForm({ guest_name: "", issue: "", resolution: "", follow_up_needed: false, owner: "", due_tomorrow: false });
  return (
    <div className="rounded-xl border border-dashed border-stone-200 p-3 space-y-2" data-testid="form-gr">
      <div className="grid sm:grid-cols-2 gap-2">
        <input value={f.guest_name} onChange={(e) => set("guest_name", e.target.value)} data-testid="gr-guest" placeholder="Guest name" className="px-3 py-1.5 rounded-lg subtle-border text-sm" />
        <input value={f.owner} onChange={(e) => set("owner", e.target.value)} data-testid="gr-owner" placeholder="Owner" className="px-3 py-1.5 rounded-lg subtle-border text-sm" />
      </div>
      <input value={f.issue} onChange={(e) => set("issue", e.target.value)} data-testid="gr-issue" placeholder="Issue" className="w-full px-3 py-1.5 rounded-lg subtle-border text-sm" />
      <textarea value={f.resolution} onChange={(e) => set("resolution", e.target.value)} data-testid="gr-resolution" placeholder="Resolution" rows={2} className="w-full px-3 py-1.5 rounded-lg subtle-border text-sm resize-none" />
      <div className="flex items-center gap-4 text-[12px] text-stone-700">
        <label><input type="checkbox" checked={f.follow_up_needed} onChange={(e) => set("follow_up_needed", e.target.checked)} /> Follow-up needed</label>
        <label><input type="checkbox" checked={f.due_tomorrow} onChange={(e) => set("due_tomorrow", e.target.checked)} /> Due tomorrow</label>
      </div>
      <button onClick={async () => { if (!f.guest_name.trim() || !f.issue.trim()) return; if(await onSubmit(f))reset(); }} data-testid="gr-add" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-stone-900 text-white text-xs">
        <Plus size={11} /> Add
      </button>
    </div>
  );
}
function GRItem({ item }) {
  return (
    <div className="flex-1">
      <div className="text-sm text-stone-900">{item.guest_name}</div>
      <div className="text-[13px] text-stone-700 mt-0.5">{item.issue} → <span className="text-stone-500">{item.resolution || "unresolved"}</span></div>
      {(item.follow_up_needed || item.due_tomorrow) && (
        <div className="text-[11px] text-amber-800 mt-1">
          {item.follow_up_needed && <>Follow-up · </>}{item.due_tomorrow && <>due tomorrow · </>}{item.owner && <>owner {item.owner}</>}
        </div>
      )}
    </div>
  );
}

function MembershipForm({ onSubmit, reference }) {
  const [f, set, reset] = useForm({ member_name: "", action: "freeze", reason: "", handled_by: "", completed_in_zenoti: false });
  return (
    <div className="rounded-xl border border-dashed border-stone-200 p-3 space-y-2" data-testid="form-ma">
      <div className="grid sm:grid-cols-2 gap-2">
        <input value={f.member_name} onChange={(e) => set("member_name", e.target.value)} data-testid="ma-name" placeholder="Member name" className="px-3 py-1.5 rounded-lg subtle-border text-sm" />
        <VesselMenu value={f.action} onChange={(value) => set("action", value)} testId="ma-action" label="Membership action" options={(reference?.membership_actions || []).map((value) => ({ value, label: value }))} />
      </div>
      <input value={f.reason} onChange={(e) => set("reason", e.target.value)} data-testid="ma-reason" placeholder="Reason" className="w-full px-3 py-1.5 rounded-lg subtle-border text-sm" />
      <div className="grid sm:grid-cols-2 gap-2 items-center">
        <input value={f.handled_by} onChange={(e) => set("handled_by", e.target.value)} data-testid="ma-handled" placeholder="Handled by" className="px-3 py-1.5 rounded-lg subtle-border text-sm" />
        <label className="text-[12px] text-stone-700"><input type="checkbox" checked={f.completed_in_zenoti} onChange={(e) => set("completed_in_zenoti", e.target.checked)} data-testid="ma-zenoti" /> Completed in Zenoti</label>
      </div>
      <button onClick={async () => { if (!f.member_name.trim()) return; if(await onSubmit(f))reset(); }} data-testid="ma-add" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-stone-900 text-white text-xs">
        <Plus size={11} /> Add
      </button>
    </div>
  );
}
function MembershipItem({ item }) {
  return (
    <div className="flex-1">
      <div className="text-sm text-stone-900">{item.member_name} · <span className="uppercase tracking-wider text-[10px] text-stone-500">{item.action}</span></div>
      {item.reason && <div className="text-[12px] text-stone-600 mt-0.5">{item.reason}</div>}
      <div className="text-[11px] text-stone-400 mt-1">
        Handled by {item.handled_by || "—"}{item.completed_in_zenoti ? " · Zenoti ✓" : " · Zenoti pending"}
      </div>
    </div>
  );
}

function InventoryForm({ onSubmit }) {
  const [f, set, reset] = useForm({ product_name: "", kind: "retail", quantity_remaining: "", needs_order: false });
  return (
    <div className="rounded-xl border border-dashed border-stone-200 p-3 space-y-2" data-testid="form-inv">
      <input value={f.product_name} onChange={(e) => set("product_name", e.target.value)} data-testid="inv-name" placeholder="Product" className="w-full px-3 py-1.5 rounded-lg subtle-border text-sm" />
      <div className="grid sm:grid-cols-3 gap-2 items-center">
        <VesselMenu value={f.kind} onChange={(value) => set("kind", value)} testId="inv-kind" label="Inventory type" options={[{ value: "retail", label: "Retail" }, { value: "non_retail", label: "Non-retail" }]} />
        <input type="number" step="0.1" value={f.quantity_remaining} onChange={(e) => set("quantity_remaining", e.target.value === "" ? "" : Number(e.target.value))} data-testid="inv-qty" placeholder="Qty remaining" className="px-3 py-1.5 rounded-lg subtle-border text-sm" />
        <label className="text-[12px] text-stone-700"><input type="checkbox" checked={f.needs_order} onChange={(e) => set("needs_order", e.target.checked)} data-testid="inv-order" /> Needs order</label>
      </div>
      <button onClick={async () => { if (!f.product_name.trim()) return; if(await onSubmit(f))reset(); }} data-testid="inv-add" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-stone-900 text-white text-xs">
        <Plus size={11} /> Add
      </button>
    </div>
  );
}
function InventoryItem({ item }) {
  return (
    <div className="flex-1">
      <div className="text-sm text-stone-900">{item.product_name} <span className="text-[10px] text-stone-400 ml-2">{item.kind}</span></div>
      <div className="text-[12px] text-stone-600 mt-0.5">Qty {item.quantity_remaining ?? "?"} {item.needs_order && <span className="text-amber-800 font-medium">· needs order</span>}</div>
    </div>
  );
}

function RecognitionForm({ onSubmit }) {
  const [f, set, reset] = useForm({ employee_name: "", what_happened: "" });
  return (
    <div className="rounded-xl border border-dashed border-emerald-100 bg-emerald-50/30 p-3 space-y-2" data-testid="form-rec">
      <p className="text-[12px] text-stone-600 italic">Did someone make today better?</p>
      <input value={f.employee_name} onChange={(e) => set("employee_name", e.target.value)} data-testid="rec-name" placeholder="Employee" className="w-full px-3 py-1.5 rounded-lg subtle-border text-sm bg-white" />
      <textarea value={f.what_happened} onChange={(e) => set("what_happened", e.target.value)} data-testid="rec-what" placeholder="What happened?" rows={2} className="w-full px-3 py-1.5 rounded-lg subtle-border text-sm bg-white resize-none" />
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-stone-500 italic">Sent for GM approval before wider sharing.</span>
        <button onClick={async () => { if (!f.employee_name.trim() || !f.what_happened.trim()) return; if(await onSubmit(f))reset(); }} data-testid="rec-add" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-stone-900 text-white text-xs">
          <Plus size={11} /> Add
        </button>
      </div>
    </div>
  );
}
function RecognitionItem({ item }) {
  return (
    <div className="flex-1">
      <div className="text-sm text-stone-900">{item.employee_name}</div>
      <div className="text-[13px] text-stone-700 mt-0.5 italic">"{item.what_happened}"</div>
      <div className="text-[10px] text-stone-400 mt-1">Status · {item.status || "pending"}</div>
    </div>
  );
}

function RoadblockForm({ onSubmit, reference }) {
  const [f, set, reset] = useForm({ category: "linen", note: "", requires_follow_up: false });
  return (
    <div className="rounded-xl border border-dashed border-stone-200 p-3 space-y-2" data-testid="form-rb">
      <div className="grid sm:grid-cols-2 gap-2">
        <VesselMenu value={f.category} onChange={(value) => set("category", value)} testId="rb-cat" label="Roadblock category" options={(reference?.roadblock_categories || []).map((value) => ({ value, label: value }))} />
        <label className="text-[12px] text-stone-700 flex items-center gap-2">
          <input type="checkbox" checked={f.requires_follow_up} onChange={(e) => set("requires_follow_up", e.target.checked)} data-testid="rb-followup" />
          Needs follow-up tomorrow
        </label>
      </div>
      <input value={f.note} onChange={(e) => set("note", e.target.value)} data-testid="rb-note" placeholder="Note (optional)" className="w-full px-3 py-1.5 rounded-lg subtle-border text-sm" />
      <button onClick={async () => { if(await onSubmit(f))reset(); }} data-testid="rb-add" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-stone-900 text-white text-xs">
        <Plus size={11} /> Add
      </button>
    </div>
  );
}
function RoadblockItem({ item }) {
  return (
    <div className="flex-1">
      <div className="text-sm text-stone-900 capitalize">{item.category}</div>
      {item.note && <div className="text-[12px] text-stone-600 mt-0.5">{item.note}</div>}
      {item.requires_follow_up && <div className="text-[11px] text-amber-800 mt-1">Needs follow-up</div>}
    </div>
  );
}

function LostFoundForm({ onSubmit }) {
  const [f, set, reset] = useForm({ item: "", description: "", where_found: "", where_stored: "", guest_contacted: false, contact_status: "" });
  return (
    <div className="rounded-xl border border-dashed border-stone-200 p-3 space-y-2" data-testid="form-lf">
      <input value={f.item} onChange={(e) => set("item", e.target.value)} data-testid="lf-item" placeholder="Item" className="w-full px-3 py-1.5 rounded-lg subtle-border text-sm" />
      <input value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" className="w-full px-3 py-1.5 rounded-lg subtle-border text-sm" />
      <div className="grid sm:grid-cols-2 gap-2">
        <input value={f.where_found} onChange={(e) => set("where_found", e.target.value)} data-testid="lf-found" placeholder="Where found" className="px-3 py-1.5 rounded-lg subtle-border text-sm" />
        <input value={f.where_stored} onChange={(e) => set("where_stored", e.target.value)} data-testid="lf-stored" placeholder="Where stored" className="px-3 py-1.5 rounded-lg subtle-border text-sm" />
      </div>
      <div className="grid sm:grid-cols-2 gap-2 items-center">
        <label className="text-[12px] text-stone-700"><input type="checkbox" checked={f.guest_contacted} onChange={(e) => set("guest_contacted", e.target.checked)} data-testid="lf-contacted" /> Guest contacted</label>
        <input value={f.contact_status} onChange={(e) => set("contact_status", e.target.value)} placeholder="Contact status" className="px-3 py-1.5 rounded-lg subtle-border text-sm" />
      </div>
      <button onClick={async () => { if (!f.item.trim()) return; if(await onSubmit(f))reset(); }} data-testid="lf-add" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-stone-900 text-white text-xs">
        <Plus size={11} /> Log item
      </button>
    </div>
  );
}
function LostFoundItem({ item }) {
  return (
    <div className="flex-1">
      <div className="text-sm text-stone-900">{item.item}</div>
      {item.description && <div className="text-[12px] text-stone-600 mt-0.5">{item.description}</div>}
      <div className="text-[11px] text-stone-500 mt-1">
        Found {item.where_found || "—"} · Stored {item.where_stored || "—"} {item.guest_contacted && <>· contacted {item.contact_status && `(${item.contact_status})`}</>}
      </div>
    </div>
  );
}


/* ─── Step 9 · Recap & Submit ─────────────────── */
function RecapStep({ report, editable, call, reference }) {
  const [text, setText] = useState(report.recap?.edited_text || "");
  const [gen, setGen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const totoRecapAvailable = Boolean(reference?.toto_recap_available);

  useEffect(() => { setText(report.recap?.edited_text || ""); }, [report.recap]);

  const generate = async () => {
    setGen(true);
    try {
      const r = await api.post("/eod/today/recap/generate");
      setText(r.data.recap.edited_text || r.data.recap.generated_text);
      await call(() => Promise.resolve({ data: r.data }));
      toast.success("Toto wrote a draft.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recap failed.");
    } finally { setGen(false); }
  };

  const saveEdit = async () => {
    await call(() => api.patch("/eod/today/recap", { edited_text: text }));
    toast.success("Recap saved.");
  };

  const submit = async () => {
    if (!text.trim()) { toast.error("Write a recap before submitting the handoff."); return; }
    setSubmitting(true);
    try {
      await api.patch("/eod/today/recap", { edited_text: text });
      const r = await api.post("/eod/today/submit");
      toast.success("Handoff submitted. Rest well.");
      await call(() => Promise.resolve({ data: r.data }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4" data-testid="recap-step">
      <p className="text-[13px] text-stone-600 leading-relaxed">
        {totoRecapAvailable
          ? "Write the handoff in your own words, or ask TOTO for an editable draft based only on this report."
          : "Write the handoff in your own words. TOTO drafting is not available in this environment."}
      </p>

      <div className="rounded-2xl bg-emerald-50/40 border border-emerald-100 p-4">
        <div className="label-eyebrow text-emerald-900 mb-2">Handoff recap</div>
        <textarea
          value={text} onChange={(e) => setText(e.target.value)} disabled={!editable}
          data-testid="recap-text" rows={7}
          placeholder="Summarize what tomorrow’s team needs to know."
          className="w-full px-3 py-2 rounded-xl border border-emerald-100 bg-white text-[14px] leading-relaxed text-stone-800 resize-none outline-none focus:border-emerald-300"
        />
        {editable && (
          <div className="mt-3 flex items-center justify-between gap-2">
            <div className="flex gap-2">
              {totoRecapAvailable && <button onClick={generate} disabled={gen} data-testid="recap-generate" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-white subtle-border text-stone-700 hover:bg-stone-50">
                <Sparkles size={11} /> {gen ? "TOTO is writing…" : text ? "Replace with TOTO draft" : "Ask TOTO for a draft"}
              </button>}
              <button onClick={saveEdit} disabled={!text.trim()} data-testid="recap-save" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-white subtle-border text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                <RefreshCw size={11} /> Save recap
              </button>
            </div>
          </div>
        )}
      </div>

      {editable && reference?.can_submit && (
        <div className="eod-recap__handoff">
          <p>Ready for tomorrow?</p>
          <button onClick={submit} disabled={submitting || !text.trim()} data-testid="eod-submit" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50">
            <Send size={13} /> {submitting ? "Signing…" : "Sign & submit handoff"}
          </button>
        </div>
      )}

      {report.status === "submitted" && (
        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 text-[13px] text-emerald-900" data-testid="eod-submitted-notice">
          Handoff submitted by {report.submitted_by_name} at {new Date(report.submitted_at).toLocaleTimeString()}.
        </div>
      )}
      {report.status === "acknowledged" && (
        <div className="rounded-2xl bg-stone-50 border border-stone-100 p-4 text-[13px] text-stone-700" data-testid="eod-ack-notice">
          Acknowledged by {report.acknowledged_by_name}.
        </div>
      )}
    </div>
  );
}
